import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../context/ToastContext';
import { useKpi } from '../../context/KpiContext';

export default function MyProgress() {
  const { showToast } = useToast();
  const { kpis, setKpis } = useKpi();

  // ========================================================
  // WFO CLOCK-IN / CLOCK-OUT TIME TRACKING
  // ========================================================
  const WORK_DURATION_HOURS = 8;
  const WORK_DURATION_MS = WORK_DURATION_HOURS * 60 * 60 * 1000;

  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weeklyAttendance, setWeeklyAttendance] = useState([
    {
      day: 'Senin',
      date: '16 Jun 2026',
      clockIn: '07:00',
      clockOut: '15:05',
      totalHours: '8j 5m',
      status: 'COMPLETE',
    },
    {
      day: 'Selasa',
      date: '17 Jun 2026',
      clockIn: '07:30',
      clockOut: '15:35',
      totalHours: '8j 5m',
      status: 'COMPLETE',
    },
    {
      day: 'Rabu',
      date: '18 Jun 2026',
      clockIn: '08:00',
      clockOut: '16:10',
      totalHours: '8j 10m',
      status: 'COMPLETE',
    },
    {
      day: 'Kamis',
      date: '19 Jun 2026',
      clockIn: '07:15',
      clockOut: '15:20',
      totalHours: '8j 5m',
      status: 'COMPLETE',
    },
    {
      day: 'Jumat',
      date: '20 Jun 2026',
      clockIn: null,
      clockOut: null,
      totalHours: '-',
      status: 'BELUM',
    },
  ]);

  // Live clock tick
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = useCallback((date) => {
    if (!date) return '--:--';
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }, []);

  const formatTimeShort = useCallback((date) => {
    if (!date) return '--:--';
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }, []);

  const getElapsedMs = useCallback(() => {
    if (!clockInTime) return 0;
    const end = clockOutTime || currentTime;
    return end - clockInTime;
  }, [clockInTime, clockOutTime, currentTime]);

  const formatDuration = useCallback((ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}j ${minutes}m ${seconds}d`;
  }, []);

  const formatDurationShort = useCallback((ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}j ${minutes}m`;
  }, []);

  const getEstimatedDeparture = useCallback(() => {
    if (!clockInTime) return null;
    return new Date(clockInTime.getTime() + WORK_DURATION_MS);
  }, [clockInTime, WORK_DURATION_MS]);

  const getRemainingMs = useCallback(() => {
    if (!clockInTime || clockOutTime) return 0;
    const remaining = WORK_DURATION_MS - getElapsedMs();
    return Math.max(0, remaining);
  }, [clockInTime, clockOutTime, WORK_DURATION_MS, getElapsedMs]);

  const getProgressPercent = useCallback(() => {
    if (!clockInTime) return 0;
    const elapsed = getElapsedMs();
    return Math.min(100, (elapsed / WORK_DURATION_MS) * 100);
  }, [clockInTime, getElapsedMs, WORK_DURATION_MS]);

  const handleClockIn = () => {
    const now = new Date();
    setClockInTime(now);
    setClockOutTime(null);
    showToast(
      `Clock-In berhasil pada ${formatTime(now)}. Selamat bekerja!`,
      'success',
    );
  };

  const handleClockOut = () => {
    const now = new Date();
    setClockOutTime(now);
    const elapsed = now - clockInTime;
    const isOvertime = elapsed > WORK_DURATION_MS;
    const overtimeMs = elapsed - WORK_DURATION_MS;

    if (isOvertime) {
      showToast(
        `Clock-Out berhasil. Anda bekerja overtime ${formatDurationShort(overtimeMs)} hari ini.`,
        'info',
      );
    } else {
      showToast(
        `Clock-Out berhasil pada ${formatTime(now)}. Total kerja: ${formatDurationShort(elapsed)}.`,
        'success',
      );
    }
  };

  const elapsedMs = getElapsedMs();
  const estimatedDeparture = getEstimatedDeparture();
  const remainingMs = getRemainingMs();
  const progressPercent = getProgressPercent();
  const isOvertime =
    clockInTime && !clockOutTime && elapsedMs > WORK_DURATION_MS;
  const canLeave =
    clockInTime && !clockOutTime && elapsedMs >= WORK_DURATION_MS;

  // ========================================================
  // STATE DEFINITIONS matching user's exact reporting style
  // ========================================================
  const [weeklyPlans, setWeeklyPlans] = useState([
    {
      id: 101,
      period: '4-8 May 2026 (Minggu Ini)',
      status: 'DRAFT', // DRAFT, SUBMITTED
      tasks: [
        {
          id: 1,
          title: 'Developed version 4 of the program timeline',
          date: 'Monday, 4 May 2026',
          evidenceUrl: 'https://docs.google.com/spreadsheets/d/example1',
          weight: 4,
          completion: 100,
          status: 'DONE',
          kpiId: 'kpi-1',
        },
        {
          id: 2,
          title: 'Conducted a simulation for VECI data input',
          date: 'Monday, 4 May 2026',
          evidenceUrl: 'https://docs.google.com/presentation/d/example2',
          weight: 3,
          completion: 100,
          status: 'DONE',
          kpiId: 'kpi-2',
        },
        {
          id: 3,
          title:
            'Designed a targeted registration strategy to engage local youth participants',
          date: '5-6 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example3',
          weight: 5,
          completion: 100,
          status: 'DONE',
          kpiId: 'kpi-1',
        },
        {
          id: 4,
          title: 'Developed output templates for the Social Mapping Report',
          date: '6-7 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example4',
          weight: 4,
          completion: 100,
          status: 'DONE',
          kpiId: 'kpi-1',
        },
        {
          id: 5,
          title:
            'Revised and refined key input points for the Social Mapping Guideline Book',
          date: 'Thursday, 7 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example5',
          weight: 3,
          completion: 100,
          status: 'DONE',
          kpiId: 'kpi-1',
        },
        {
          id: 6,
          title:
            'Created performance and program evaluation instruments on the CLP website',
          date: 'Thursday, 7 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example6',
          weight: 5,
          completion: 100,
          status: 'DONE',
          kpiId: 'kpi-1',
        },
        {
          id: 7,
          title:
            'Broke down and defined the roles of the BCF team in executing the registration strategy',
          date: 'Thursday, 7 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example7',
          weight: 3,
          completion: 100,
          status: 'DONE',
        },
        {
          id: 8,
          title:
            'Developed a recruitment strategy for Field Supervising Lecturers',
          date: 'Friday, 8 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example8',
          weight: 4,
          completion: 100,
          status: 'DONE',
        },
        {
          id: 9,
          title:
            'Designed a three-layer interview process for candidate selection',
          date: 'Friday, 8 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example9',
          weight: 4,
          completion: 100,
          status: 'DONE',
        },
        {
          id: 10,
          title:
            'Prepared the minutes of Mr. Jimmy’s dissertation defense Q&A session',
          date: 'Tuesday, 5 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example10',
          weight: 2,
          completion: 100,
          status: 'DONE',
        },
        {
          id: 11,
          title: 'Participated in a program-specific discussion with Mr. Jimmy',
          date: 'Thursday, 7 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example11',
          weight: 2,
          completion: 100,
          status: 'DONE',
        },
        {
          id: 12,
          title: 'Joined a PRD one-pager discussion with the KM and IT',
          date: '8 May 2026',
          evidenceUrl: 'https://docs.google.com/document/d/example12',
          weight: 2,
          completion: 80,
          status: 'IN_PROGRESS',
        },
      ],
    },
    {
      id: 102,
      period: '11-13 May 2026 (Rencana Depan)',
      status: 'DRAFT',
      tasks: [
        {
          id: 13,
          title:
            'Discuss Campus Leaders Program implementation 2026 with Head Program',
          date: 'TBA',
          evidenceUrl: '',
          weight: 3,
          completion: 0,
          status: 'TODO',
        },
        {
          id: 14,
          title:
            'Conduct discussions regarding DPL account ownership, as well as the DPL satisfaction and impact evaluation system',
          date: 'Monday, 11 May 2026',
          evidenceUrl: '',
          weight: 4,
          completion: 0,
          status: 'TODO',
        },
        {
          id: 15,
          title: 'Adjust the registration timeline',
          date: 'Monday, 11 May 2026',
          evidenceUrl: '',
          weight: 2,
          completion: 0,
          status: 'TODO',
        },
        {
          id: 16,
          title: 'Revise the monthly capacity building materials and timeline',
          date: 'Monday, 11 May 2026',
          evidenceUrl: '',
          weight: 3,
          completion: 0,
          status: 'TODO',
        },
        {
          id: 17,
          title:
            'Develop the monthly activity plan and expected outputs for Project Officers',
          date: '11-13 May 2026',
          evidenceUrl: '',
          weight: 5,
          completion: 0,
          status: 'TODO',
        },
        {
          id: 18,
          title: 'Continue developing the CLP introduction deck',
          date: '11-13 May 2026',
          evidenceUrl: '',
          weight: 4,
          completion: 0,
          status: 'TODO',
        },
        {
          id: 19,
          title:
            'Join a knowledge-sharing session on the PFI consultant workflow with Mr. Setyo',
          date: 'Wednesday, 13 May 2026',
          evidenceUrl: '',
          weight: 3,
          completion: 0,
          status: 'TODO',
        },
      ],
    },
  ]);

  const [activePlanId, setActivePlanId] = useState(101);

  // Form State
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [newPlanTitle, setNewPlanTitle] = useState('');
  const [newPlanDesc, setNewPlanDesc] = useState('');
  const [newPlanDate, setNewPlanDate] = useState('');
  const [newPlanWeight, setNewPlanWeight] = useState(3);
  const [newPlanTargetUrl, setNewPlanTargetUrl] = useState('');
  const [newPlanKpiId, setNewPlanKpiId] = useState('');

  // ========================================================
  // KPI - milik saya secara keseluruhan, tidak terikat ke satu periode.
  // Task di periode manapun bisa dihubungkan ke KPI yang sama, dan actual
  // dihitung otomatis dari progress semua task yang terhubung, lintas periode.
  // Data KPI sendiri (title/unit/target) dibagikan lewat KpiContext supaya
  // konsisten dengan yang dikelola manager di Employee Detail.
  // ========================================================
  const [showKpiForm, setShowKpiForm] = useState(false);
  const [newKpiTitle, setNewKpiTitle] = useState('');
  const [newKpiUnit, setNewKpiUnit] = useState('');
  const [newKpiTarget, setNewKpiTarget] = useState('');

  // ========================================================
  // HANDLERS
  // ========================================================
  const handleUpdateTaskProgress = (planId, taskId, newCompletion) => {
    let newStatus = 'TODO';
    if (newCompletion > 0 && newCompletion < 100) {
      newStatus = 'IN_PROGRESS';
    } else if (newCompletion === 100) {
      newStatus = 'DONE';
    }

    setWeeklyPlans((prev) =>
      prev.map((plan) => {
        if (plan.id === planId) {
          return {
            ...plan,
            tasks: plan.tasks.map((task) => {
              if (task.id === taskId) {
                return {
                  ...task,
                  completion: newCompletion,
                  status: newStatus,
                };
              }
              return task;
            }),
          };
        }
        return plan;
      }),
    );
  };

  const handleUpdateEvidenceUrl = (planId, taskId, newUrl) => {
    setWeeklyPlans((prev) =>
      prev.map((plan) => {
        if (plan.id === planId) {
          return {
            ...plan,
            tasks: plan.tasks.map((task) => {
              if (task.id === taskId) {
                return { ...task, evidenceUrl: newUrl };
              }
              return task;
            }),
          };
        }
        return plan;
      }),
    );
  };

  const formatPlanDate = (isoDate) => {
    if (!isoDate) return 'TBA';
    const d = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('id-ID', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleAddTaskPlan = (e) => {
    e.preventDefault();
    if (!newPlanTitle) return;

    setWeeklyPlans((prev) =>
      prev.map((plan) => {
        if (plan.id === activePlanId) {
          const newId =
            plan.tasks.length > 0
              ? Math.max(...plan.tasks.map((t) => t.id)) + 1
              : 1;
          return {
            ...plan,
            tasks: [
              ...plan.tasks,
              {
                id: newId,
                title: newPlanTitle,
                date: formatPlanDate(newPlanDate),
                evidenceUrl: newPlanTargetUrl,
                weight: parseInt(newPlanWeight),
                completion: 0,
                status: 'TODO',
                kpiId: newPlanKpiId || null,
              },
            ],
          };
        }
        return plan;
      }),
    );

    setNewPlanTitle('');
    setNewPlanDesc('');
    setNewPlanDate('');
    setNewPlanWeight(3);
    setNewPlanTargetUrl('');
    setNewPlanKpiId('');
    setShowTaskForm(false);
    showToast('Aktivitas baru berhasil didaftarkan!', 'success');
  };

  const handleSubmitPlan = (planId) => {
    setWeeklyPlans((prev) =>
      prev.map((plan) => {
        if (plan.id === planId) {
          return { ...plan, status: 'SUBMITTED' };
        }
        return plan;
      }),
    );
    showToast(
      'Laporan Progress & Plan mingguan berhasil dikirim ke manajer!',
      'success',
    );
  };

  // ========================================================
  // KPI HANDLERS
  // ========================================================
  const handleAddKpi = (e) => {
    e.preventDefault();
    if (!newKpiTitle || !newKpiTarget) return;

    const newId = `kpi-${Date.now()}`;
    setKpis((prev) => [
      ...prev,
      {
        id: newId,
        title: newKpiTitle,
        unit: newKpiUnit || 'unit',
        target: parseFloat(newKpiTarget),
      },
    ]);

    setNewKpiTitle('');
    setNewKpiUnit('');
    setNewKpiTarget('');
    setShowKpiForm(false);
    showToast('KPI baru berhasil ditambahkan!', 'success');
  };

  const handleDeleteKpi = (kpiId) => {
    const isUsed = weeklyPlans.some((plan) =>
      plan.tasks.some((t) => t.kpiId === kpiId),
    );
    if (isUsed) {
      showToast(
        'KPI tidak bisa dihapus karena masih terhubung dengan aktivitas.',
        'error',
      );
      return;
    }
    setKpis((prev) => prev.filter((kpi) => kpi.id !== kpiId));
  };

  // ========================================================
  // CALCULATIONS
  // ========================================================
  const currentPlan =
    weeklyPlans.find((p) => p.id === activePlanId) || weeklyPlans[0];
  const totalPlannedWeight = currentPlan.tasks.reduce(
    (sum, t) => sum + t.weight,
    0,
  );
  const totalAchievedWeight = currentPlan.tasks.reduce(
    (sum, t) => sum + t.weight * (t.completion / 100),
    0,
  );
  const achievementRate =
    totalPlannedWeight > 0
      ? (totalAchievedWeight / totalPlannedWeight) * 100
      : 0;
  const doneTasksCount = currentPlan.tasks.filter(
    (t) => t.status === 'DONE',
  ).length;

  const kpiSummaries = kpis.map((kpi) => {
    // Actual dihitung otomatis dari total progress task manapun (lintas periode)
    // yang terhubung ke KPI ini
    const linkedTasks = weeklyPlans.flatMap((plan) =>
      plan.tasks.filter((t) => t.kpiId === kpi.id),
    );
    const actual = linkedTasks.reduce(
      (sum, t) => sum + (t.completion || 0) / 100,
      0,
    );
    return {
      ...kpi,
      actual,
      achievement: kpi.target > 0 ? Math.min(200, (actual / kpi.target) * 100) : 0,
      linkedTasksCount: linkedTasks.length,
    };
  });
  const overallKpiAchievement =
    kpiSummaries.length > 0
      ? kpiSummaries.reduce((sum, k) => sum + Math.min(100, k.achievement), 0) /
        kpiSummaries.length
      : 0;

  return (
    <div className='container-fluid p-0'>
      {/* HEADER HERO */}
      <div
        className='card border-0 shadow-sm rounded-4 mb-4 text-white overflow-hidden'
        style={{
          background:
            'linear-gradient(135deg, #1f4068 0%, #162447 50%, #1b1a17 100%)',
        }}
      >
        <div className='card-body p-4 p-md-5'>
          <div className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
            <div>
              <span
                className='badge bg-warning text-dark rounded-pill px-3 py-1.5 fw-semibold mb-3'
                style={{ fontSize: '0.8rem' }}
              >
                <i className='bi bi-person-workspace me-1'></i> FORMAT LAPORAN
                MINGGUAN: PROGRESS & PLAN
              </span>
              <h2 className='fw-bold text-white mb-2'>
                My Weekly Progress & Plan Workspace
              </h2>
              <p className='text-white-50 mb-0 max-w-2xl small'>
                Format transparan untuk melaporkan capaian mingguan (*Progress*)
                dan target minggu depan (*Plan*), terintegrasi dengan bobot
                aktivitas (*Weight*) untuk mengukur produktivitas kerja
                objektif.
              </p>
            </div>
            <div className='d-flex gap-3 flex-wrap justify-content-end'>
              <div
                className='bg-white-10 backdrop-blur rounded-4 p-4 text-center border border-white-10'
                style={{ minWidth: '170px' }}
              >
                <span className='text-white-50 small d-block mb-1'>
                  Indeks Velocity
                </span>
                <h1 className='fw-bold text-warning mb-0'>
                  {totalAchievedWeight.toFixed(1)}
                </h1>
                <span className='badge bg-light text-dark rounded-pill mt-2'>
                  {achievementRate.toFixed(0)}% Capaian Tugas
                </span>
              </div>
              <div
                className='bg-white-10 backdrop-blur rounded-4 p-4 text-center border border-white-10'
                style={{ minWidth: '170px' }}
              >
                <span className='text-white-50 small d-block mb-1'>
                  Capaian KPI
                </span>
                <h1 className='fw-bold text-info mb-0'>
                  {overallKpiAchievement.toFixed(0)}%
                </h1>
                <span className='badge bg-light text-dark rounded-pill mt-2'>
                  {kpiSummaries.length} KPI Aktif
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DANGER/WARNING ACTION INFO BAR */}
      <div className='alert alert-primary border-0 rounded-4 p-3 mb-4 d-flex align-items-center gap-3 shadow-sm'>
        <i className='bi bi-info-circle-fill fs-4 text-primary'></i>
        <div className='flex-grow-1'>
          <h6 className='fw-bold text-primary-emphasis mb-1'>
            Panduan Pengisian Laporan
          </h6>
          <p className='mb-0 text-muted small'>
            Status laporan saat ini: <strong>{currentPlan.status}</strong>.
            Pastikan setiap aktivitas yang selesai (*DONE*) dilengkapi dengan
            tautan bukti hasil kerja (*Evidence Link* / <strong>Here</strong>)
            agar manajer dapat memverifikasi output Anda.
          </p>
        </div>
        {currentPlan.status === 'DRAFT' && (
          <button
            onClick={() => handleSubmitPlan(currentPlan.id)}
            className='btn btn-primary btn-sm rounded-pill px-4 align-self-center font-semibold shadow-sm'
          >
            <i className='bi bi-send-fill me-1'></i> Kirim Laporan ke Manajer
          </button>
        )}
      </div>

      {/* KPI SECTION - milik saya secara keseluruhan, berlaku lintas periode */}
      <div className='card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white'>
        <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2'>
          <div>
            <h6 className='fw-bold text-dark mb-1'>
              <i className='bi bi-bullseye text-primary me-2'></i>
              KPI Saya
            </h6>
            <span className='text-muted small'>
              Berlaku untuk semua periode. Nilai aktual dihitung otomatis dari
              progress aktivitas/tugas manapun yang terhubung ke tiap KPI.
            </span>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-outline-primary rounded-pill px-3'
            onClick={() => setShowKpiForm((prev) => !prev)}
          >
            <i className='bi bi-plus-lg me-1'></i> Tambah KPI
          </button>
        </div>

        {kpiSummaries.length === 0 ? (
          <div className='text-center py-4 text-muted'>
            <i className='bi bi-bullseye fs-2 d-block mb-2 text-secondary'></i>
            Belum ada KPI.
          </div>
        ) : (
          <div className='row g-3 mb-3'>
            {kpiSummaries.map((kpi) => (
              <div
                className='col-md-6 col-xl-4'
                key={kpi.id}
              >
                <div className='p-3 rounded-3 bg-light-subtle border h-100'>
                  <div className='d-flex justify-content-between align-items-start mb-2'>
                    <div>
                      <strong className='text-dark d-block'>
                        {kpi.title}
                      </strong>
                      <span className='text-muted small'>
                        {kpi.linkedTasksCount} aktivitas terhubung (semua
                        periode)
                      </span>
                    </div>
                    <button
                      type='button'
                      className='btn btn-sm btn-link text-danger p-0'
                      title='Hapus KPI'
                      onClick={() => handleDeleteKpi(kpi.id)}
                    >
                      <i className='bi bi-trash'></i>
                    </button>
                  </div>

                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <strong className='text-dark'>
                      {Number.isInteger(kpi.actual)
                        ? kpi.actual
                        : kpi.actual.toFixed(1)}
                    </strong>
                    <span className='text-muted small'>
                      / {kpi.target} {kpi.unit}
                    </span>
                  </div>

                  <div
                    className='progress'
                    style={{ height: '6px' }}
                  >
                    <div
                      className={`progress-bar ${kpi.achievement >= 100 ? 'bg-success' : kpi.achievement >= 50 ? 'bg-primary' : 'bg-warning'}`}
                      role='progressbar'
                      style={{ width: `${Math.min(100, kpi.achievement)}%` }}
                    />
                  </div>
                  <span className='small fw-bold text-muted d-block text-end mt-1'>
                    {kpi.achievement.toFixed(0)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {showKpiForm && (
          <form
            onSubmit={handleAddKpi}
            className='row g-2 align-items-end border-top pt-3'
          >
            <div className='col-md-4'>
              <label className='form-label small fw-semibold text-muted mb-1'>
                Judul KPI *
              </label>
              <input
                type='text'
                className='form-control form-control-sm rounded-3'
                placeholder='contoh: Leads Baru'
                value={newKpiTitle}
                onChange={(e) => setNewKpiTitle(e.target.value)}
                required
              />
            </div>
            <div className='col-md-2'>
              <label className='form-label small fw-semibold text-muted mb-1'>
                Satuan
              </label>
              <input
                type='text'
                className='form-control form-control-sm rounded-3'
                placeholder='leads, %, dok'
                value={newKpiUnit}
                onChange={(e) => setNewKpiUnit(e.target.value)}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label small fw-semibold text-muted mb-1'>
                Target *
              </label>
              <input
                type='number'
                className='form-control form-control-sm rounded-3'
                value={newKpiTarget}
                onChange={(e) => setNewKpiTarget(e.target.value)}
                min='0'
                required
              />
            </div>
            <div className='col-md-3 d-flex gap-2'>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm rounded-pill w-100'
                onClick={() => setShowKpiForm(false)}
              >
                Batal
              </button>
              <button
                type='submit'
                className='btn btn-primary btn-sm rounded-pill w-100 fw-semibold'
              >
                Simpan
              </button>
            </div>
          </form>
        )}
      </div>

      {/* WORKSPACE CONTENT GRID */}
      <div className='row g-4'>
        {/* LEFT COLUMN: ACTIVE WORKSPACE & TASK LIST */}
        <div className='col-lg-8'>
          <div className='card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white'>
            <div className='d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2'>
              <div>
                <span className='text-muted small d-block'>
                  PERIODE YANG AKTIF
                </span>
                <select
                  className='form-select form-select-lg border-0 fw-bold text-dark p-0 bg-transparent shadow-none'
                  style={{
                    fontSize: '1.25rem',
                    width: 'auto',
                    cursor: 'pointer',
                  }}
                  value={activePlanId}
                  onChange={(e) => setActivePlanId(parseInt(e.target.value))}
                >
                  {weeklyPlans.map((p) => (
                    <option
                      key={p.id}
                      value={p.id}
                    >
                      {p.period}
                    </option>
                  ))}
                </select>
              </div>
              <div className='d-flex align-items-center gap-2'>
                <span
                  className={`badge ${currentPlan.status === 'SUBMITTED' ? 'bg-success text-white' : 'bg-warning text-dark'} rounded-pill px-3 py-2`}
                  style={{ fontSize: '0.8rem' }}
                >
                  {currentPlan.status === 'SUBMITTED'
                    ? 'TERKIRIM (Read-Only)'
                    : 'DRAFT (Bisa Diedit)'}
                </span>
                {currentPlan.status === 'DRAFT' && (
                  <button
                    type='button'
                    className='btn btn-primary btn-sm rounded-pill px-3 fw-semibold'
                    onClick={() => setShowTaskForm(true)}
                  >
                    <i className='bi bi-plus-lg me-1'></i> Tambah Aktivitas
                  </button>
                )}
              </div>
            </div>

            {/* TASK INTERACTIVE WORKSPACE */}
            <div className='table-responsive'>
              <table className='table table-hover align-middle table-borderless'>
                <thead>
                  <tr className='text-muted small border-bottom'>
                    <th style={{ width: '40%' }}>
                      Aktivitas / Deskripsi Laporan
                    </th>
                    <th style={{ width: '15%' }}>Hari / Tanggal</th>
                    <th style={{ width: '15%' }}>KPI Terkait</th>
                    <th
                      className='text-center'
                      style={{ width: '10%' }}
                    >
                      Bobot
                    </th>
                    <th style={{ width: '15%' }}>Bukti (Link / Here)</th>
                    <th style={{ width: '15%' }}>Progress & Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPlan.tasks.length === 0 ? (
                    <tr>
                      <td
                        colSpan='6'
                        className='text-center py-5 text-muted'
                      >
                        <i className='bi bi-journal-x fs-2 d-block mb-2 text-secondary'></i>
                        Belum ada aktivitas yang tercatat untuk periode ini.
                      </td>
                    </tr>
                  ) : (
                    currentPlan.tasks.map((task) => (
                      <tr
                        key={task.id}
                        className='border-bottom-subtle'
                      >
                        <td>
                          <div
                            className='fw-semibold text-dark mb-1'
                            style={{ fontSize: '0.925rem' }}
                          >
                            {task.title}
                          </div>
                          <span
                            className={`badge px-2 py-1 ${
                              task.status === 'DONE'
                                ? 'bg-success-subtle text-success'
                                : task.status === 'IN_PROGRESS'
                                  ? 'bg-warning-subtle text-warning'
                                  : 'bg-secondary-subtle text-secondary'
                            }`}
                            style={{ fontSize: '0.7rem' }}
                          >
                            {task.status}
                          </span>
                        </td>
                        <td>
                          <span className='text-secondary small d-block'>
                            {task.date}
                          </span>
                        </td>
                        <td>
                          {task.kpiId ? (
                            <span
                              className='badge bg-primary-subtle text-primary px-2 py-1'
                              style={{ fontSize: '0.7rem' }}
                            >
                              <i className='bi bi-bullseye me-1'></i>
                              {kpis.find((k) => k.id === task.kpiId)?.title ||
                                '-'}
                            </span>
                          ) : (
                            <span className='text-muted small'>-</span>
                          )}
                        </td>
                        <td className='text-center'>
                          <span
                            className='badge bg-light text-dark border px-2.5 py-1.5 fw-semibold'
                            style={{ fontSize: '0.85rem' }}
                          >
                            {task.weight}
                          </span>
                        </td>
                        <td>
                          {currentPlan.status === 'DRAFT' ? (
                            <input
                              type='text'
                              className='form-control form-control-sm rounded-3'
                              placeholder='https://...'
                              value={task.evidenceUrl}
                              onChange={(e) =>
                                handleUpdateEvidenceUrl(
                                  currentPlan.id,
                                  task.id,
                                  e.target.value,
                                )
                              }
                            />
                          ) : task.evidenceUrl ? (
                            <a
                              href={task.evidenceUrl}
                              target='_blank'
                              rel='noreferrer'
                              className='btn btn-link btn-sm text-primary fw-semibold p-0'
                            >
                              <i className='bi bi-link-45deg me-1'></i> Here
                            </a>
                          ) : (
                            <span className='text-muted small'>TBA</span>
                          )}
                        </td>
                        <td>
                          {currentPlan.status === 'DRAFT' ? (
                            <div className='d-flex flex-column gap-1'>
                              <input
                                type='range'
                                className='form-range'
                                min='0'
                                max='100'
                                step='10'
                                value={task.completion}
                                onChange={(e) =>
                                  handleUpdateTaskProgress(
                                    currentPlan.id,
                                    task.id,
                                    parseInt(e.target.value),
                                  )
                                }
                              />
                              <span className='small fw-bold text-muted text-end'>
                                {task.completion}%
                              </span>
                            </div>
                          ) : (
                            <div className='d-flex align-items-center gap-2'>
                              <div
                                className='progress flex-grow-1'
                                style={{ height: '6px' }}
                              >
                                <div
                                  className={`progress-bar ${task.completion === 100 ? 'bg-success' : 'bg-primary'}`}
                                  role='progressbar'
                                  style={{ width: `${task.completion}%` }}
                                />
                              </div>
                              <span className='small fw-bold text-muted'>
                                {task.completion}%
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: TIME TRACKING, METRICS & ADD PLAN */}
        <div className='col-lg-4'>
          {/* WFO CLOCK-IN / CLOCK-OUT CARD */}
          <div className='card border-0 shadow-sm rounded-4 mb-4 overflow-hidden'>
            <div
              className='card-header border-0 text-white p-3'
              style={{
                background:
                  clockInTime && !clockOutTime
                    ? canLeave
                      ? 'linear-gradient(135deg, #11998e, #38ef7d)'
                      : 'linear-gradient(135deg, #667eea, #764ba2)'
                    : clockOutTime
                      ? 'linear-gradient(135deg, #3a7bd5, #00d2ff)'
                      : 'linear-gradient(135deg, #434343, #000000)',
              }}
            >
              <div className='d-flex align-items-center justify-content-between'>
                <div>
                  <h6 className='fw-bold mb-0 text-white'>
                    <i className='bi bi-clock-history me-2'></i>
                    WFO Time Tracker
                  </h6>
                  <span className='text-white-50 small'>
                    Durasi kerja standar: {WORK_DURATION_HOURS} jam
                  </span>
                </div>
                <div className='text-end'>
                  <span className='text-white-50 small d-block'>
                    Live Clock
                  </span>
                  <strong
                    className='text-white font-monospace'
                    style={{ fontSize: '1.1rem' }}
                  >
                    {formatTime(currentTime)}
                  </strong>
                </div>
              </div>
            </div>
            <div className='card-body p-3'>
              {/* CLOCK IN / OUT BUTTONS */}
              {!clockInTime && !clockOutTime && (
                <div className='text-center py-3'>
                  <i className='bi bi-door-open fs-1 text-muted d-block mb-2'></i>
                  <p className='text-muted small mb-3'>
                    Anda belum Clock-In hari ini.
                  </p>
                  <button
                    onClick={handleClockIn}
                    className='btn btn-success rounded-pill px-5 py-2 fw-semibold shadow-sm'
                  >
                    <i className='bi bi-box-arrow-in-right me-2'></i> Clock-In
                    Sekarang
                  </button>
                </div>
              )}

              {clockInTime && !clockOutTime && (
                <div>
                  {/* Live Timer Display */}
                  <div className='text-center mb-3'>
                    <span className='text-muted small d-block mb-1'>
                      DURASI KERJA HARI INI
                    </span>
                    <h2
                      className={`fw-bold font-monospace mb-1 ${isOvertime ? 'text-danger' : 'text-dark'}`}
                    >
                      {formatDuration(elapsedMs)}
                    </h2>
                    {isOvertime && (
                      <span className='badge bg-danger-subtle text-danger rounded-pill px-3 py-1'>
                        <i className='bi bi-exclamation-triangle-fill me-1'></i>
                        Overtime +
                        {formatDurationShort(elapsedMs - WORK_DURATION_MS)}
                      </span>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className='mb-3'>
                    <div className='d-flex justify-content-between small text-muted mb-1'>
                      <span>Clock-In: {formatTimeShort(clockInTime)}</span>
                      <span>Target: {formatTimeShort(estimatedDeparture)}</span>
                    </div>
                    <div
                      className='progress rounded-pill'
                      style={{ height: '10px' }}
                    >
                      <div
                        className={`progress-bar rounded-pill ${progressPercent >= 100 ? 'bg-success' : 'bg-primary'}`}
                        role='progressbar'
                        style={{
                          width: `${Math.min(progressPercent, 100)}%`,
                          transition: 'width 1s linear',
                        }}
                      />
                    </div>
                    <div className='text-center mt-1'>
                      <small className='text-muted'>
                        {canLeave
                          ? '✅ Anda sudah memenuhi 8 jam kerja. Boleh Clock-Out kapan saja.'
                          : `Sisa: ${formatDurationShort(remainingMs)} lagi`}
                      </small>
                    </div>
                  </div>

                  {/* Estimated Departure Highlight */}
                  <div
                    className={`p-3 rounded-3 mb-3 d-flex align-items-center gap-3 ${canLeave ? 'bg-success-subtle border border-success-subtle' : 'bg-light border'}`}
                  >
                    <i
                      className={`bi ${canLeave ? 'bi-door-open-fill text-success' : 'bi-clock'} fs-4`}
                    ></i>
                    <div>
                      <span className='small text-muted d-block'>
                        ESTIMASI JAM PULANG
                      </span>
                      <strong
                        className={`fs-5 ${canLeave ? 'text-success' : 'text-dark'}`}
                      >
                        {formatTimeShort(estimatedDeparture)}
                      </strong>
                      {canLeave && (
                        <span className='text-success small d-block'>
                          Anda sudah boleh pulang!
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleClockOut}
                    className='btn btn-outline-danger rounded-pill w-100 py-2 fw-semibold'
                  >
                    <i className='bi bi-box-arrow-right me-2'></i> Clock-Out
                  </button>
                </div>
              )}

              {clockOutTime && (
                <div className='text-center py-2'>
                  <i className='bi bi-check-circle-fill fs-1 text-success d-block mb-2'></i>
                  <h5 className='fw-bold text-dark mb-1'>Sesi Kerja Selesai</h5>
                  <div className='d-flex justify-content-center gap-4 mt-3 text-center'>
                    <div>
                      <span className='text-muted small d-block'>Clock-In</span>
                      <strong className='text-dark'>
                        {formatTimeShort(clockInTime)}
                      </strong>
                    </div>
                    <div>
                      <span className='text-muted small d-block'>
                        Clock-Out
                      </span>
                      <strong className='text-dark'>
                        {formatTimeShort(clockOutTime)}
                      </strong>
                    </div>
                    <div>
                      <span className='text-muted small d-block'>Total</span>
                      <strong className='text-primary'>
                        {formatDurationShort(elapsedMs)}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* WEEKLY ATTENDANCE LOG */}
            <div className='card-footer bg-light-subtle border-top p-3'>
              <h6 className='fw-bold small text-dark mb-2'>
                <i className='bi bi-calendar-week me-1'></i> Log Kehadiran
                Minggu Ini
              </h6>
              <div className='d-flex flex-column gap-1'>
                {weeklyAttendance.map((att, idx) => (
                  <div
                    key={idx}
                    className='d-flex justify-content-between align-items-center py-1 px-2 rounded-2 small'
                    style={{
                      background:
                        att.status === 'COMPLETE' ? '#f0fdf4' : '#fef9ee',
                    }}
                  >
                    <div className='d-flex align-items-center gap-2'>
                      <i
                        className={`bi ${att.status === 'COMPLETE' ? 'bi-check-circle-fill text-success' : 'bi-dash-circle text-warning'}`}
                      ></i>
                      <span className='fw-semibold text-dark'>{att.day}</span>
                      <span className='text-muted'>{att.date}</span>
                    </div>
                    <div className='d-flex align-items-center gap-3'>
                      <span
                        className='text-muted font-monospace'
                        style={{ fontSize: '0.8rem' }}
                      >
                        {att.clockIn || '--:--'} → {att.clockOut || '--:--'}
                      </span>
                      <span
                        className={`badge ${att.status === 'COMPLETE' ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'} rounded-pill`}
                        style={{ fontSize: '0.7rem', minWidth: '55px' }}
                      >
                        {att.totalHours}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* STATS OVERVIEW CARD */}
          <div className='card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white'>
            <h6 className='fw-bold text-dark mb-3'>
              <i className='bi bi-pie-chart-fill text-primary me-2'></i>
              Metrik Produktivitas Kerja
            </h6>
            <div className='d-flex flex-column gap-3'>
              <div className='p-3 rounded-3 bg-light-subtle border d-flex justify-content-between align-items-center'>
                <div>
                  <span className='text-muted small d-block'>
                    TOTAL BOBOT TUGAS
                  </span>
                  <strong className='text-dark fs-5'>
                    {totalPlannedWeight} pts
                  </strong>
                </div>
                <div className='bg-primary-subtle text-primary p-2.5 rounded-circle'>
                  <i className='bi bi-list-task fs-5'></i>
                </div>
              </div>
              <div className='p-3 rounded-3 bg-light-subtle border d-flex justify-content-between align-items-center'>
                <div>
                  <span className='text-muted small d-block'>
                    BOBOT TERCAPAI (VELOCITY)
                  </span>
                  <strong className='text-success fs-5'>
                    {totalAchievedWeight.toFixed(1)} pts
                  </strong>
                </div>
                <div className='bg-success-subtle text-success p-2.5 rounded-circle'>
                  <i className='bi bi-check2-circle fs-5'></i>
                </div>
              </div>
              <div className='p-3 rounded-3 bg-light-subtle border d-flex justify-content-between align-items-center'>
                <div>
                  <span className='text-muted small d-block'>
                    TUGAS YANG SELESAI
                  </span>
                  <strong className='text-dark fs-5'>
                    {doneTasksCount} / {currentPlan.tasks.length} tugas
                  </strong>
                </div>
                <div className='bg-info-subtle text-info p-2.5 rounded-circle'>
                  <i className='bi bi-card-checklist fs-5'></i>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* MODAL: RENCANAKAN / TAMBAH AKTIVITAS */}
      {showTaskForm && (
        <>
          <div
            className='modal fade show d-block'
            tabIndex='-1'
            role='dialog'
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1090 }}
          >
            <div
              className='modal-dialog modal-dialog-centered'
              role='document'
            >
              <div className='modal-content border-0 shadow-lg rounded-4'>
                <div className='modal-header border-0 p-4 pb-0'>
                  <h6 className='fw-bold text-dark mb-0'>
                    <i className='bi bi-plus-circle-fill text-primary me-2'></i>
                    Rencanakan / Tambah Aktivitas
                  </h6>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={() => setShowTaskForm(false)}
                    aria-label='Close'
                  ></button>
                </div>
                <div className='modal-body p-4'>
                  <form
                    onSubmit={handleAddTaskPlan}
                    className='d-flex flex-column gap-3'
                  >
                    <div>
                      <label className='form-label small fw-semibold text-muted mb-1'>
                        Aktivitas / Deskripsi Laporan *
                      </label>
                      <input
                        type='text'
                        className='form-control rounded-3'
                        placeholder='contoh: Developed version 4 of the program timeline'
                        value={newPlanTitle}
                        onChange={(e) => setNewPlanTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className='form-label small fw-semibold text-muted mb-1'>
                        Hari / Tanggal Pelaksanaan *
                      </label>
                      <input
                        type='date'
                        className='form-control rounded-3'
                        value={newPlanDate}
                        onChange={(e) => setNewPlanDate(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className='form-label small fw-semibold text-muted mb-1'>
                        Evidence Link (Tautan Hasil) / "Here"
                      </label>
                      <input
                        type='text'
                        className='form-control rounded-3'
                        placeholder='contoh: https://docs.google.com/...'
                        value={newPlanTargetUrl}
                        onChange={(e) => setNewPlanTargetUrl(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className='form-label small fw-semibold text-muted mb-1'>
                        KPI Terkait (opsional)
                      </label>
                      <select
                        className='form-select rounded-3'
                        value={newPlanKpiId}
                        onChange={(e) => setNewPlanKpiId(e.target.value)}
                      >
                        <option value=''>Tanpa KPI</option>
                        {kpiSummaries.map((kpi) => (
                          <option
                            key={kpi.id}
                            value={kpi.id}
                          >
                            {kpi.title} ({Number.isInteger(kpi.actual) ? kpi.actual : kpi.actual.toFixed(1)}/{kpi.target} {kpi.unit})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className='form-label small fw-semibold text-muted mb-1'>
                        Bobot Kesulitan (1 - 5) *
                      </label>
                      <select
                        className='form-select rounded-3'
                        value={newPlanWeight}
                        onChange={(e) => setNewPlanWeight(e.target.value)}
                      >
                        <option value='1'>1 (Sangat Mudah / Diskusi Pendek)</option>
                        <option value='2'>2 (Mudah / Meeting + Catatan)</option>
                        <option value='3'>
                          3 (Sedang / Pembuatan Draf / Koordinasi)
                        </option>
                        <option value='4'>
                          4 (Tinggi / Desain / Penyusunan Strategi)
                        </option>
                        <option value='5'>
                          5 (Sangat Tinggi / Coding Inti / Dokumen Final)
                        </option>
                      </select>
                    </div>
                    <div className='d-flex gap-2 mt-2'>
                      <button
                        type='button'
                        className='btn btn-outline-secondary rounded-pill w-100 py-2 fw-semibold'
                        onClick={() => setShowTaskForm(false)}
                      >
                        Batal
                      </button>
                      <button
                        type='submit'
                        className='btn btn-primary rounded-pill w-100 py-2 fw-semibold'
                      >
                        <i className='bi bi-plus-lg me-1'></i> Tambah Rencana
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div
            className='modal-backdrop fade show'
            style={{ zIndex: 1080 }}
          ></div>
        </>
      )}
    </div>
  );
}
